import LogManager from "../../utils/logManager";

// Merges `patch` into the newest chat message (the agent turn being streamed).
const patchLastMessage = (setChatMessages, patch) =>
  setChatMessages((prev) => (prev.length === 0
    ? prev
    : [...prev.slice(0, -1), { ...prev[prev.length - 1], ...patch }]));

export async function invokeAgentStream({ requestBody, signal, setChatMessages, agentId, agent }) {
  const startedAt = Date.now();
  const turnId = crypto.randomUUID();
  const metricDimensions = {
    agentType: agent?.extensionId ? "extension" : "built-in",
  };
  const logOutcome = (outcome, details = {}) => {
    LogManager.info("Assistant response outcome:", {
      outcome,
      turnId,
      sessionId: requestBody.sessionId,
      agentId,
      elapsedMs: Date.now() - startedAt,
      ...details,
    });
  };

  let response;
  try {
    const bodyString = JSON.stringify(requestBody);
    const bodyBytes = new TextEncoder().encode(bodyString);
    const hashBuffer = await crypto.subtle.digest("SHA-256", bodyBytes);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const bodyHash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

    const requestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-amz-content-sha256": bodyHash,
      },
      body: bodyString,
      signal,
    };

    response = await fetch("/invokeAgent", { ...requestInit, redirect: "manual" });

    if (response.status === 307 || response.type === "opaqueredirect") {
      response = await fetch("/invokeAgent", requestInit);
    }
  } catch (err) {
    const aborted = err.name === "AbortError";
    LogManager.metric(
      aborted ? "Assistant_Response_Aborted" : "Assistant_Response_Error",
      1,
      aborted ? metricDimensions : { ...metricDimensions, phase: "request", errorCode: err.name || "REQUEST_ERROR" }
    );
    logOutcome(aborted ? "ABORTED" : "ERROR", {
      phase: "request",
      errorCode: err.name || "REQUEST_ERROR",
    });
    throw err;
  }

  if (!response.ok) {
    // Pre-stream errors arrive as a JSON body { error, message } with a non-200
    // status. Surface the backend's customer-facing `message` when present.
    let serverMessage;
    let errorCode;
    try {
      const errBody = await response.json();
      serverMessage = errBody?.message;
      errorCode = errBody?.error;
    } catch (bodyErr) {
      // Non-JSON body — fall back to the status code.
    }
    const err = new Error(serverMessage || `HTTP ${response.status}`);
    err.serverMessage = serverMessage;
    err.errorCode = errorCode;
    err.phase = "pre-stream";
    LogManager.metric("Assistant_Response_Error", 1, {
      ...metricDimensions,
      phase: err.phase,
      errorCode: errorCode || "HTTP_ERROR",
    });
    logOutcome("ERROR", {
      phase: err.phase,
      errorCode: errorCode || "HTTP_ERROR",
      httpStatus: response.status,
    });
    throw err;
  }

  setChatMessages((prev) => [...prev, { role: "agent", text: "", ...(agentId && { agentId, agent }), createdAt: Date.now() }]);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let fullText = "";
  let buffer = "";
  let readCount = 0;
  let parseErrorCount = 0;
  let firstTextReceived = false;

  const recordFirstText = () => {
    if (firstTextReceived) return;
    firstTextReceived = true;
    LogManager.metric("Assistant_FirstTextToken_Received", 1, metricDimensions);
  };

  // Parse a single stream line and apply it. Shared by the read loop and the
  // trailing-buffer flush so both paths stay in sync; throws on backend error
  // payloads so callers surface them instead of silently dropping the turn.
  const processLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    // Success chunks are SSE `data:` lines; in-band errors (once the 200 status
    // is already committed) arrive as a bare JSON line { error, message }.
    const payload = trimmed.startsWith("data:") ? trimmed.substring(5).trim() : trimmed;
    if (!payload.startsWith("{")) return;
    try {
      const json = JSON.parse(payload);
      if (json?.error && typeof json?.message === "string") {
        const err = new Error(json.message);
        err.serverMessage = json.message;
        err.errorCode = json.error;
        err.phase = "in-stream";
        throw err;
      }
      const deltaText = json?.event?.contentBlockDelta?.delta?.text;
      if (deltaText) {
        recordFirstText();
        fullText += deltaText;
        patchLastMessage(setChatMessages, { text: fullText });
      }
      const msgContent = json?.message?.content;
      if (json?.message?.role === "assistant" && Array.isArray(msgContent)) {
        for (const block of msgContent) {
          if (block.text && !fullText) {
            recordFirstText();
            fullText = block.text;
            patchLastMessage(setChatMessages, { text: fullText });
          }
        }
      }
    } catch (parseErr) {
      // Re-throw backend error payloads; only swallow genuine JSON parse failures.
      if (parseErr?.serverMessage || parseErr?.errorCode) throw parseErr;
      parseErrorCount += 1;
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        // Flush any trailing line that lacked a newline terminator — e.g. a final
        // in-stream error chunk — so it isn't silently dropped from the buffer.
        processLine(buffer);
        buffer = "";
        break;
      }

      readCount += 1;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();
      for (const line of lines) {
        processLine(line);
      }
    }
  } catch (err) {
    if (parseErrorCount > 0) {
      LogManager.metric("Assistant_Response_ParseError", parseErrorCount, metricDimensions);
    }
    if (err.name === "AbortError") {
      LogManager.metric("Assistant_Response_Aborted", 1, metricDimensions);
      logOutcome("ABORTED", {
        readCount,
        responseCharCount: fullText.length,
        parseErrorCount,
      });
      throw err;
    }
    LogManager.metric("Assistant_Response_Error", 1, {
      ...metricDimensions,
      phase: err.phase || "stream-read",
      errorCode: err.errorCode || err.name || "UNKNOWN_ERROR",
    });
    logOutcome("ERROR", {
      phase: err.phase || "stream-read",
      errorCode: err.errorCode || err.name || "UNKNOWN_ERROR",
      readCount,
      responseCharCount: fullText.length,
      parseErrorCount,
    });
    throw err;
  } finally {
    // Cancel the reader on every exit path so a mid-stream throw doesn't leave the
    // reader locked and the underlying response body/connection un-cancelled.
    reader.cancel().catch(() => {});
  }

  const completedTimestamp = Math.floor(Date.now() / 1000);
  patchLastMessage(setChatMessages, { timestamp: completedTimestamp });

  if (parseErrorCount > 0) {
    LogManager.metric("Assistant_Response_ParseError", parseErrorCount, metricDimensions);
  }

  if (fullText) {
    LogManager.metric("Assistant_Response_Completed", 1, metricDimensions);
    LogManager.metric("Assistant_Response_Latency", Date.now() - startedAt, metricDimensions);
    LogManager.metric("Assistant_Response_Characters", fullText.length, metricDimensions);
    LogManager.metric("Assistant_Response_Reads", readCount, metricDimensions);
    logOutcome("SUCCESS", {
      readCount,
      responseCharCount: fullText.length,
      parseErrorCount,
    });
  } else {
    LogManager.metric("Assistant_Response_Empty", 1, metricDimensions);
    logOutcome("EMPTY", { readCount, responseCharCount: 0, parseErrorCount });
    patchLastMessage(setChatMessages, { text: "(No response)", timestamp: completedTimestamp });
  }
}
